// Copyright 2015-2023 Nstream, inc.
// All rights reserved.

import type {Observes} from "@swim/util";
import {Format} from "@swim/codec";
import type {Value} from "@swim/structure";
import {Length} from "@swim/math";
import {ValueDownlink} from "@swim/client";
import type {Trait} from "@swim/model";
import {ViewRef} from "@swim/view";
import {TraitViewRef} from "@swim/controller";
import type {PanelView} from "@swim/panel";
import {PanelController} from "@swim/panel";
import {ColLayout} from "@swim/table";
import {TableLayout} from "@swim/table";
import {TextCellView} from "@swim/table";
import {RowView} from "@swim/table";
import {TableView} from "@swim/table";
import {Look} from "@swim/theme";

/** @public */
export class PulseAgentCountController extends PanelController {
  @TraitViewRef({
    extends: true,
    initView(panelView: PanelView): void {
      super.initView(panelView);
      panelView.style.setIntrinsic({
        display: "flex",
        alignItems: "center",
      });
      this.owner.table.insertView(panelView);
    },
    viewDidMount(panelView: PanelView): void {
      this.owner.consume(panelView);
    },
    viewWillUnmount(panelView: PanelView): void {
      this.owner.unconsume(panelView);
    },
    createView(): PanelView {
      return (super.createView() as PanelView).setIntrinsic({
        panelStyle: "card",
        headerTitle: "Agents",
      });
    },
  })
  override readonly panel!: TraitViewRef<this, Trait, PanelView> & PanelController["panel"] & Observes<PanelView>;

  @ViewRef({
    viewType: TableView,
    viewKey: true,
    initView(tableView: TableView): void {
      tableView.setIntrinsic({
        style: {
          flexGrow: 1,
        },
        glows: false,
      });
      this.owner.pulseAgentCountRow.insertView(tableView);
    },
    createLayout(): TableLayout {
      const cols = new Array<ColLayout>();
      cols.push(ColLayout.create("value", 1, 0, 0, false, false, Look.textColor));
      return new TableLayout(null, null, null, Length.px(36), cols);
    },
    createView(): TableView {
      return (super.createView() as TableView).setIntrinsic({
        style: {
          marginTop: 30,
          marginRight: 12,
          marginBottom: 30,
          marginLeft: 12,
        },
        rowHeight: 100,
        layout: this.createLayout(),
      });
    },
  })
 readonly table!: ViewRef<this, TableView> & {
    createLayout(): TableLayout,
  };

  @ViewRef({
    viewType: RowView,
    createView(): RowView {
      const rowView = RowView.create();
      const leafView = rowView.leaf.insertView();
      (leafView.getOrCreateCell("value", TextCellView)).setIntrinsic({
        style: {
          justifyContent: "center",
        },
        content: "",
      });
      return rowView;
    },
  })
  readonly pulseAgentCountRow!: ViewRef<this, RowView>;

  @ValueDownlink({
    laneUri: "pulse",
    consumed: true,
    didSet(value: Value): void {
      //console.log("PulseAgentCountController.pulseDownlink.didSet:", value.toLike());
      const agents = value.get("agents");
      const agentCount = agents.get("agentCount").numberValue(0);
      const agentCountRow = this.owner.pulseAgentCountRow.attachView();
      agentCountRow.getOrCreateCell("value", TextCellView).set({
        style: {
          fontSize: 60,
        },
        content: Format.prefix(agentCount, 1),
      });
    },
  })
  readonly pulseDownlink!: ValueDownlink<this>;
}
